// src/pages/ActivityPage.jsx

import React, { useState } from 'react';
import { useParams, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Importa los componentes de actividad
import TwelveAreasActivity from '../../components/es/activities/TwelveAreasActivity';
import AwarenessAreasActivity from '../../components/es/activities/AwarenessAreasActivity';
import FutureLookBackActivity from '../../components/es/activities/FutureLookBackActivity';
import SevenLevelsDeepWhyActivity from '../../components/es/activities/SevenLevelsDeepWhyActivity';
import CoachingSessionPage from '../../components/es/activities/CoachingSessionPage';
import MeditationSevenPhases from '../../components/es/activities/meditation/MeditationSevenPhases';
import NivelesConcienciaActivity from '../../components/es/activities/NivelesConcienciaActivity';
import BeliefThresholdActivity from '../../components/es/activities/BeliefThresholdActivity';

// Importa los datos desde el archivo central
import { allActivities, courseInfo } from '../../data/es/CourseStructure';

const activityComponentMap = {
  '12-areas-balance': TwelveAreasActivity,
  'concientizando-areas-balance': AwarenessAreasActivity,
  'viendo-hacia-atras-desde-el-futuro': FutureLookBackActivity,
  '7-niveles-deep-why': SevenLevelsDeepWhyActivity,
  'primera-sesion-de-coaching': CoachingSessionPage,
  'meditacion-7-fases': MeditationSevenPhases,
  'niveles-conciencia-hawkins': NivelesConcienciaActivity,
  'umbral-de-creencia': BeliefThresholdActivity,
  // ...agrega aquí el resto de tus componentes de actividad
};

const ActivityPage = () => {
  const { activitySlug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  let activitySlug = paramSlug;
  if (!activitySlug) {
    const pathParts = location.pathname.split('/');
    const potentialSlug = pathParts[pathParts.length - 1];
    if (activityComponentMap[potentialSlug]) activitySlug = potentialSlug;
  }

  const CurrentActivityComponent = activityComponentMap[activitySlug];
  const currentActivityIndex = allActivities.findIndex(act => act.slug === activitySlug);
  const currentActivity = allActivities[currentActivityIndex];

  if (!CurrentActivityComponent || !currentActivity) {
    if (!activitySlug) {
        console.error("ActivityPage: No se pudo determinar un slug de actividad válido desde la URL:", location.pathname);
        return <Navigate to="/cursos/manifiesta" replace />;
    }
    return (
      <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4 text-center">
        <h1 className="text-3xl text-red-500">Actividad no encontrada</h1>
        <p className="text-gray-400 mt-2">La actividad con la ruta '{activitySlug}' no es válida o no existe.</p>
        <Link to="/cursos/manifiesta" className="mt-4 text-yellow-400 hover:text-yellow-300">
          Volver al temario del programa
        </Link>
      </div>
    );
  }

  const nextActivity = allActivities[currentActivityIndex + 1];

  const handleProceedToNext = () => {
    if (!user) {
      setShowSavePrompt(true);
    } else if (nextActivity) {
      navigate(`/cursos/manifiesta/${nextActivity.slug}`);
    }
  };
  
  const handleContinueAsGuest = () => {
    setShowSavePrompt(false);
    if (nextActivity) {
      navigate(`/cursos/manifiesta/${nextActivity.slug}`);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6 md:p-8">
      {showSavePrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-gray-900 border border-yellow-500/30 rounded-2xl p-8 max-w-lg text-center shadow-2xl shadow-yellow-500/10 relative">
                  <button onClick={() => setShowSavePrompt(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white text-2xl leading-none">×</button>
                  <h2 className="text-3xl font-bold text-yellow-400 mb-4">¡Excelente, sigamos avanzando!</h2>
                  <p className="text-gray-300 mb-6">Para guardar tu progreso de forma segura y acceder desde cualquier dispositivo, te recomendamos <strong>crear una cuenta gratis</strong>.</p>
                  <p className="text-gray-300 mb-8">Si prefieres, puedes continuar como invitado. Tu avance se guardará únicamente en este navegador.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to={`/signup?redirect=${location.pathname}`} className="w-full sm:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-transform transform hover:scale-105">Crear Cuenta Gratis</Link>
                      <Link to={`/login?redirect=${location.pathname}`} className="w-full sm:w-auto bg-gray-700 text-white font-bold py-3 px-8 rounded-lg hover:bg-gray-600">Ya tengo cuenta</Link>
                  </div>
                  <div className="mt-8">
                      <button onClick={handleContinueAsGuest} className="text-gray-400 hover:text-white transition-colors duration-200 text-sm font-semibold">
                          Continuar como invitado y guardar en este navegador
                      </button>
                  </div>
              </div>
          </div>
      )}

      <header className="max-w-4xl mx-auto mb-8">
        <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600">
          {currentActivity.title}
        </h1>
        <Link to="/cursos/manifiesta" className="inline-block mt-4 text-sm text-gray-400 hover:text-yellow-300">
          {courseInfo.backToCourseLinkText}
        </Link>
      </header>

      <main className="max-w-4xl mx-auto">
        <CurrentActivityComponent nextActivity={nextActivity} onProceed={handleProceedToNext} />
      </main>
    </div>
  );
};

export default ActivityPage;